import { Link } from "react-router-dom";
import { articles } from "@/mocks/articles";

export default function ArticleMatrix() {
  const featuredArticles = articles.slice(0, 5);
  const moreArticles = articles.slice(5);

  return (
    <section className="relative w-full bg-background-50 py-16 md:py-20">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-12">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold whitespace-nowrap rounded-full bg-primary-100 text-primary-700">
            <i className="ri-guide-line text-sm"></i>
            Latest Guides
          </span>
          <h2 className="mt-4 font-heading text-2xl md:text-4xl font-bold text-foreground-950">
            Buying Guides &amp; Resources
          </h2>
          <p className="mt-2 text-sm text-foreground-500">
            Expert-curated Pandabuy Spreadsheet guides to level up your shopping
          </p>
        </div>

        {/* Original 5: Creative Matrix Layout */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {/* Featured Card (spans 2 cols) */}
          <Link
            to={featuredArticles[0].slug}
            className="md:col-span-2 group cursor-pointer"
          >
            <div className="rounded-lg overflow-hidden bg-background-100 h-full flex flex-col md:flex-row">
              <div className="md:w-2/5 relative overflow-hidden">
                <img
                  src={featuredArticles[0].imageUrl}
                  alt={featuredArticles[0].title}
                  className="w-full h-48 md:h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="md:w-3/5 p-5 md:p-6 flex flex-col justify-center">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-xs font-medium text-primary-600">{featuredArticles[0].category}</span>
                  <span className="text-xs text-foreground-400">{featuredArticles[0].date}</span>
                </div>
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 group-hover:text-primary-600 transition-colors leading-snug">
                  {featuredArticles[0].title}
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2">
                  {featuredArticles[0].excerpt}
                </p>
                <span className="mt-3 text-xs font-semibold text-foreground-500">
                  {featuredArticles[0].readTime}
                </span>
              </div>
            </div>
          </Link>

          {/* Text Card 1 */}
          <Link to={featuredArticles[1].slug} className="group cursor-pointer">
            <div className="rounded-lg bg-secondary-100/50 p-5 md:p-6 h-full flex flex-col">
              <div>
                <i className="ri-double-quotes-l text-3xl text-secondary-300"></i>
              </div>
              <div className="flex items-center gap-2 mt-2 mb-3">
                <span className="text-xs font-medium text-accent-600">{featuredArticles[1].category}</span>
                <span className="text-xs text-foreground-400">{featuredArticles[1].date}</span>
              </div>
              <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 group-hover:text-primary-600 transition-colors leading-snug">
                {featuredArticles[1].title}
              </h3>
              <p className="mt-2 text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-3 flex-1">
                {featuredArticles[1].excerpt}
              </p>
              <span className="mt-3 text-xs font-semibold text-foreground-500">
                {featuredArticles[1].readTime}
              </span>
            </div>
          </Link>

          {/* Image Card 2 */}
          <Link to={featuredArticles[2].slug} className="group cursor-pointer">
            <div className="rounded-lg overflow-hidden bg-background-100 h-full flex flex-col">
              <div className="relative overflow-hidden h-40">
                <img
                  src={featuredArticles[2].imageUrl}
                  alt={featuredArticles[2].title}
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent"></div>
                <div className="absolute bottom-0 left-0 right-0 p-4">
                  <h3 className="font-heading text-sm md:text-base font-bold text-white leading-snug line-clamp-2">
                    {featuredArticles[2].title}
                  </h3>
                  <span className="text-xs text-white/70 mt-1 block">{featuredArticles[2].readTime}</span>
                </div>
              </div>
            </div>
          </Link>

          {/* Wide Image Card 4 */}
          <Link to={featuredArticles[4].slug} className="md:col-span-2 group cursor-pointer">
            <div className="rounded-lg overflow-hidden relative h-52 md:h-60">
              <img
                src={featuredArticles[4].imageUrl}
                alt={featuredArticles[4].title}
                className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent"></div>
              <div className="absolute bottom-0 left-0 p-5 md:p-6 max-w-md">
                <h3 className="font-heading text-lg md:text-xl font-bold text-white leading-snug">
                  {featuredArticles[4].title}
                </h3>
                <p className="mt-2 text-xs md:text-sm text-white/75 leading-relaxed line-clamp-2">
                  {featuredArticles[4].excerpt}
                </p>
                <span className="text-xs text-white/60 mt-2 block">{featuredArticles[4].readTime}</span>
              </div>
            </div>
          </Link>

          {/* Text Card 3 */}
          <Link to={featuredArticles[3].slug} className="group cursor-pointer">
            <div className="rounded-lg bg-secondary-100/50 p-5 md:p-6 h-full flex flex-col">
              <div>
                <i className="ri-double-quotes-l text-3xl text-secondary-300"></i>
              </div>
              <div className="flex items-center gap-2 mt-2 mb-3">
                <span className="text-xs font-medium text-accent-600">{featuredArticles[3].category}</span>
                <span className="text-xs text-foreground-400">{featuredArticles[3].date}</span>
              </div>
              <h3 className="font-heading text-base md:text-lg font-bold text-foreground-950 group-hover:text-primary-600 transition-colors leading-snug">
                {featuredArticles[3].title}
              </h3>
              <p className="mt-2 text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-3 flex-1">
                {featuredArticles[3].excerpt}
              </p>
              <span className="mt-3 text-xs font-semibold text-foreground-500">
                {featuredArticles[3].readTime}
              </span>
            </div>
          </Link>
        </div>

        {/* More Guides: 9 new articles */}
        <div className="mt-16 md:mt-20">
          <div className="text-center mb-8 md:mb-10">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-semibold whitespace-nowrap rounded-full bg-accent-100 text-accent-700">
              <i className="ri-article-line text-sm"></i>
              More Guides
            </span>
            <h3 className="mt-3 font-heading text-xl md:text-2xl font-bold text-foreground-950">
              Explore All Pandabuy Sheet Categories
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {moreArticles.map((article) => (
              <Link
                key={article.id}
                to={article.slug}
                className="group cursor-pointer"
              >
                <div className="rounded-lg overflow-hidden bg-background-100 h-full flex flex-col">
                  <div className="relative overflow-hidden aspect-[16/10]">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute top-3 left-3">
                      <span className="px-2.5 py-1 text-[10px] font-semibold whitespace-nowrap rounded-full bg-background-50/90 text-foreground-700">
                        {article.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-4 md:p-5 flex flex-col flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-foreground-400">{article.date}</span>
                      <span className="text-xs text-foreground-300">·</span>
                      <span className="text-xs text-foreground-400">{article.readTime}</span>
                    </div>
                    <h3 className="font-heading text-sm md:text-base font-bold text-foreground-950 group-hover:text-primary-600 transition-colors leading-snug flex-1">
                      {article.title}
                    </h3>
                    <span className="mt-3 text-xs font-semibold text-primary-600 group-hover:text-primary-700 transition-colors">
                      Read Article →
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}